import PropTypes from 'prop-types';
import {
  Col, Row, Tag, Timeline, Typography,
} from 'antd';
import React from 'react';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import nth from '../../../utils/nth';
import { DEFAULT_DATE_TIME, DEFAULT_TIME } from '../../../utils/constants/timeFormats';
import {
  estimatedEtaPropTypes, geofencingAssetPropTypes, monitoringOrderStopAssetSnapshotPropTypes, stopPropTypes,
} from '../../../types';
import { StopStatus } from '../../../store/normalizers/orders.normalizer';
import StopLocation from './StopLocation';
import { getStopType } from '../../../types/mappers';
import TripStopDetails from './TripStopDetails';
import { behaviourTagLabelKey, displayBehaviourTags, isOperationalStop } from '../../../types/stopBehaviour.type';
import { OrderStopType } from '../../../types/stop.type';

/*
 * A display-only status, deliberately not added to the domain StopStatus enum: it describes how the
 * timeline dot is drawn, not where the stop is in its lifecycle.
 */
const NON_OPERATIONAL = 'NON_OPERATIONAL';

const HollowDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 2px solid ${({ theme }) => theme.color.gray2};
  border-radius: 50%;
  background: transparent;
`;

const BehaviourTag = styled(Tag)`
  margin-left: 6px;
  font-size: 11px;
  line-height: 16px;
`;

const dotColorByStatus = {
  [StopStatus.PASSED]: 'green',
  [StopStatus.ONGOING]: 'blue',
  [StopStatus.FUTURE]: 'blue',
  [NON_OPERATIONAL]: 'gray',
};

const dotIconByStatus = {
  [StopStatus.PASSED]: <CheckCircleOutlined />,
  [StopStatus.ONGOING]: null,
  [StopStatus.FUTURE]: null,
  [NON_OPERATIONAL]: <HollowDot />,
};

function determineEndDateString(t, { startMoment, endMoment }) {
  if (startMoment.isSame(endMoment)) {
    return (<span className="greyed">{t('ORDER.STOP.FIXED')}</span>);
  }

  if (startMoment.clone().startOf('d').isSame(endMoment.clone().startOf('d'))) {
    return endMoment.format(DEFAULT_TIME);
  }

  return endMoment.format(DEFAULT_DATE_TIME);
}

const TripDetailsLine = styled(Typography.Paragraph)`
  margin-bottom: 3px !important;
  
  .greyed {
    color: ${({ theme }) => theme.color.gray2}
  }
`;

const TripDetailsHeaderLine = styled(TripDetailsLine)`
  font-size: 14px;
  margin-bottom: 3px !important;
`;

/*
 * The green-check PASSED dot asserts that a required operation completed. That claim is only
 * meaningful for an operational stop: visited status is not tracked for the others server-side, and
 * geofencing data merely says a vehicle passed through. Untagged stops are operational, so this
 * returns exactly what it always did for them.
 */
const determineStopStatus = (hasGeofencingData, stop) => {
  if (!isOperationalStop(stop)) {
    return NON_OPERATIONAL;
  }
  return hasGeofencingData ? StopStatus.PASSED : StopStatus.FUTURE;
};

const TripStop = ({
  stop, index, geofencingWithEtaEnabled, eta, hasGeofencingData, geofencingData, stopAssetSnapshots,
}) => {
  const { t } = useTranslation();
  const stopType = getStopType(stop);
  const displayStatus = determineStopStatus(hasGeofencingData, stop);
  // Empty for an untagged stop, so nothing is added to the header. A set difference rather than an
  // is-operational test, so {OPERATIONAL, SERVICE} still shows its Service chip.
  const behaviourTags = displayBehaviourTags(stop);
  /*
   * "2nd stop: Not set [Waypoint]" reads as a contradiction. When a labelled stop has no load or
   * unload flags the stop type carries no information, so the label speaks for it instead.
   * Only ever true for a labelled stop, so untagged headers are untouched.
   */
  const showStopType = stopType !== OrderStopType.UNKNOWN || behaviourTags.length === 0;
  return (
    <Timeline.Item
      key={stop.id}
      dot={dotIconByStatus[displayStatus]}
      color={dotColorByStatus[displayStatus]}
    >
      <Row>
        <Col xs={12}>
          <TripDetailsHeaderLine>
            {t('ORDER.STOP_INDEX', { index: (index + 1) + nth(index + 1) })}
            {stopType && showStopType && `: ${t(`ORDER.STOP_TYPES.${stopType}`)}`}
            {behaviourTags.map((tag) => (
              <BehaviourTag key={tag}>{t(behaviourTagLabelKey(tag))}</BehaviourTag>
            ))}
          </TripDetailsHeaderLine>
          <TripDetailsLine>
            <b>
              {`${stop.timeSlot.startMoment.format(DEFAULT_DATE_TIME)} – `}
              {determineEndDateString(t, stop.timeSlot)}
            </b>
          </TripDetailsLine>
          <TripDetailsLine>
            {stop.name}
          </TripDetailsLine>
          <TripDetailsLine>
            <StopLocation
              place={stop.place}
              latitude={stop.latitude}
              longitude={stop.longitude}
            />
          </TripDetailsLine>
          <TripDetailsLine>
            {stop.additionalInfo}
          </TripDetailsLine>
        </Col>
        <Col xs={12}>
          <TripStopDetails
            stop={stop}
            geofencingWithEtaEnabled={geofencingWithEtaEnabled}
            eta={eta}
            hasGeofencingData={hasGeofencingData}
            geofencingData={geofencingData}
            stopAssetSnapshots={stopAssetSnapshots}
          />
        </Col>
      </Row>
    </Timeline.Item>
  );
};

TripStop.propTypes = {
  stop: stopPropTypes.isRequired,
  index: PropTypes.number.isRequired,
  geofencingWithEtaEnabled: PropTypes.bool.isRequired,
  eta: PropTypes.shape({
    loading: PropTypes.bool,
    data: PropTypes.arrayOf(estimatedEtaPropTypes),
    onCalculate: PropTypes.func,
    tooltipKey: PropTypes.string,
    disabled: PropTypes.bool,
  }).isRequired,
  geofencingData: PropTypes.arrayOf(
    geofencingAssetPropTypes,
  ),
  stopAssetSnapshots: PropTypes.shape({
    hasData: PropTypes.bool,
    data: PropTypes.arrayOf(monitoringOrderStopAssetSnapshotPropTypes),
  }).isRequired,
  hasGeofencingData: PropTypes.bool.isRequired,
};

TripStop.defaultProps = {
  geofencingData: {},
};

export default TripStop;
